import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/HeaderV2";
import DatePicker from "../components/DatePicker";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/StatsV2.module.css";
import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    Rectangle,
} from "recharts";

import { useKiosk } from "../KioskContext";
import { MemberType, useMember } from "../MemberContext";
import CollectionHistoryModal from "../components/CollectionHistoryModal";
import LoadingIndicator from "../components/LoadingIndicator";
import { collectionErrorMessage, readCollectionStats, type CollectionFilters, type CollectionStats } from "../utils/collectionHistory";

type Mode = "ALL" | "KIOSK" | "USER";
type SelectableRow = {
    id: string;
    columns: string[];
};

const CARBON_REDUCTION_GRAM_PER_INPUT = 66;

const fmtYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const hasTimestamp = <T extends { timestamp?: number }>(x: T): x is T & { timestamp: number } =>
    typeof x.timestamp === "number";

const startOfDay = (ms: number) => {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const endOfDay = (ms: number) => {
    const d = new Date(ms);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
};

const enumerateDays = (start: Date, end: Date) => {
    const days: string[] = [];
    const d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const e = new Date(end);
    e.setHours(0, 0, 0, 0);
    while (d.getTime() <= e.getTime()) {
        days.push(fmtYMD(d));
        d.setDate(d.getDate() + 1);
    }
    return days;
};

const groupByDay = <T extends { timestamp?: number }>(rows: T[], dateKey: keyof T) => {
    const map: Record<string, number> = {};
    for (const r of rows) {
        const ms = r[dateKey] as number;
        if (!ms) continue;
        const ymd = fmtYMD(new Date(ms));
        map[ymd] = (map[ymd] || 0) + 1;
    }
    return map;
};

const toDailySeries = (days: string[], dailyMap: Record<string, number>) =>
    days.map((d) => ({ date: d, value: dailyMap[d] || 0 }));

const AdminStats: React.FC = () => {
    const { kiosks } = useKiosk();
    const { members: users } = useMember();
    const [collectionStats, setCollectionStats] = useState<CollectionStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState("");
    const [reloadStats, setReloadStats] = useState(0);
    const [historyDate, setHistoryDate] = useState<string | null>(null);

    const [startDate, setStartDate] = useState<Date>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 29);
        return d;
    });
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [mode, setMode] = useState<Mode>("ALL");
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [selectUsersOpen, setSelectUsersOpen] = useState<boolean>(false);
    const [selectedKiosks, setSelectedKiosks] = useState<Set<string>>(new Set());
    const [selectKiosksOpen, setSelectKiosksOpen] = useState<boolean>(false);
    const filters = useMemo<CollectionFilters>(() => mode === "KIOSK" && selectedKiosks.size
        ? { kioskIds: Array.from(selectedKiosks).sort() }
        : mode === "USER" && selectedUsers.size ? { ownerIds: Array.from(selectedUsers).sort() } : {}, [mode, selectedKiosks, selectedUsers]);

    const invalidDateRange = startDate.getTime() > endDate.getTime();
    const days = useMemo(() => (invalidDateRange ? [] : enumerateDays(startDate, endDate)), [startDate, endDate, invalidDateRange]);
    const s = startOfDay(startDate.getTime());
    const e = endOfDay(endDate.getTime());

    const kiosksInRange = useMemo(
        () => kiosks.filter(hasTimestamp).filter((k) => k.timestamp >= s && k.timestamp <= e),
        [kiosks, s, e]
    );
    const usersInRange = useMemo(
        () => users.filter((u: MemberType) => u.timestamp >= s && u.timestamp <= e),
        [users, s, e]
    );
    useEffect(() => {
        let active = true;
        setCollectionStats(null);
        setStatsError("");
        setHistoryDate(null);
        if (invalidDateRange) { setStatsLoading(false); return; }
        setStatsLoading(true);
        void readCollectionStats({ startDate: fmtYMD(new Date(s)), endDate: fmtYMD(new Date(e)), ...filters })
            .then((result) => { if (active) setCollectionStats(result); })
            .catch((error) => { if (active) setStatsError(collectionErrorMessage(error)); })
            .finally(() => { if (active) setStatsLoading(false); });
        return () => { active = false; };
    }, [s, e, filters, invalidDateRange, reloadStats]);

    const userDaily = useMemo(() => toDailySeries(days, groupByDay(usersInRange, "timestamp")), [days, usersInRange]);
    const kioskDaily = useMemo(
        () => toDailySeries(days, groupByDay(kiosksInRange, "timestamp")),
        [days, kiosksInRange]
    );
    const receiptDaily = useMemo(
        () => {
            const counts = new Map((collectionStats?.days || []).map((day) => [day.date, day.count]));
            return days.map((date) => ({ date, value: counts.get(date) || 0 }));
        },
        [collectionStats, days]
    );

    const totalReceipts = collectionStats?.count || 0;
    const totalInputAmt = collectionStats?.quantity || 0;
    const esgSavedKg = (totalInputAmt * CARBON_REDUCTION_GRAM_PER_INPUT) / 1000;
    const modeSummary = getModeSummary(mode, selectedKiosks.size, selectedUsers.size);

    const setPreset = (preset: "TODAY" | "LAST_7" | "LAST_30" | "THIS_MONTH") => {
        const today = new Date();
        if (preset === "TODAY") {
            setStartDate(new Date(startOfDay(today.getTime())));
            setEndDate(new Date(endOfDay(today.getTime())));
        }
        if (preset === "LAST_7") {
            const start = new Date(today);
            start.setDate(start.getDate() - 6);
            setStartDate(new Date(startOfDay(start.getTime())));
            setEndDate(new Date(endOfDay(today.getTime())));
        }
        if (preset === "LAST_30") {
            const start = new Date(today);
            start.setDate(start.getDate() - 29);
            setStartDate(new Date(startOfDay(start.getTime())));
            setEndDate(new Date(endOfDay(today.getTime())));
        }
        if (preset === "THIS_MONTH") {
            setStartDate(new Date(startOfDay(new Date(today.getFullYear(), today.getMonth(), 1).getTime())));
            setEndDate(new Date(endOfDay(today.getTime())));
        }
    };

    const changeMode = (nextMode: Mode) => {
        setMode(nextMode);
        if (nextMode !== "KIOSK") setSelectedKiosks(new Set());
        if (nextMode !== "USER") setSelectedUsers(new Set());
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>관리자 통계</h4>
                    <p className={common["text--copy"]}>
                        기간과 집계 기준을 선택해 회원, 키오스크, 회수 데이터를 확인합니다.
                    </p>

                    <section className={style["filter-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>통계 조건</p>
                                <h5 className={style["panel-title"]}>집계 기준 설정</h5>
                                <p className={style["panel-copy"]}>
                                    {fmtYMD(startDate)} ~ {fmtYMD(endDate)} · {modeSummary}
                                </p>
                            </div>
                            <div className={style["mode-segment"]}>
                                <button className={mode === "ALL" ? style["segment--active"] : ""} onClick={() => changeMode("ALL")}>
                                    전체
                                </button>
                                <button
                                    className={mode === "KIOSK" ? style["segment--active"] : ""}
                                    onClick={() => changeMode("KIOSK")}
                                >
                                    키오스크별
                                </button>
                                <button className={mode === "USER" ? style["segment--active"] : ""} onClick={() => changeMode("USER")}>
                                    유저별
                                </button>
                            </div>
                        </div>

                        <div className={style["preset-row"]}>
                            <button onClick={() => setPreset("TODAY")}>오늘</button>
                            <button onClick={() => setPreset("LAST_7")}>최근 7일</button>
                            <button onClick={() => setPreset("LAST_30")}>최근 30일</button>
                            <button onClick={() => setPreset("THIS_MONTH")}>이번 달</button>
                        </div>

                        <div className={style["wrapper--date-time"]}>
                            <DatePicker label="시작일" value={startDate} onChange={setStartDate} />
                            <DatePicker label="종료일" value={endDate} onChange={setEndDate} />
                        </div>

                        <div className={style["target-row"]}>
                            {mode === "KIOSK" && (
                                <>
                                    <button className={style["button--primary"]} onClick={() => setSelectKiosksOpen(true)}>
                                        키오스크 선택 ({selectedKiosks.size}개)
                                    </button>
                                    <button className={style["button--secondary"]} onClick={() => setSelectedKiosks(new Set())}>
                                        선택 초기화
                                    </button>
                                </>
                            )}
                            {mode === "USER" && (
                                <>
                                    <button className={style["button--primary"]} onClick={() => setSelectUsersOpen(true)}>
                                        유저 선택 ({selectedUsers.size}명)
                                    </button>
                                    <button className={style["button--secondary"]} onClick={() => setSelectedUsers(new Set())}>
                                        선택 초기화
                                    </button>
                                </>
                            )}
                            {mode === "ALL" && <span className={style["target-copy"]}>전체 데이터를 기준으로 집계합니다.</span>}
                        </div>

                        {invalidDateRange && <div className={style["status-message"]}>시작일을 종료일 이전으로 선택해주세요.</div>}
                    </section>

                    <div className={style["summary-grid"]}>
                        <SummaryCard title="기간 내 신규 회원" value={usersInRange.length.toLocaleString()} unit="명" />
                        <SummaryCard title="기간 내 설치 키오스크" value={kiosksInRange.length.toLocaleString()} unit="대" />
                        <SummaryCard title="총 회수 건수" loading={statsLoading} value={statsError ? "—" : totalReceipts.toLocaleString()} unit={statsLoading || statsError ? "" : "건"} />
                        <SummaryCard
                            title="탄소 절감량"
                            loading={statsLoading}
                            value={statsError ? "—" : esgSavedKg.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}
                            unit={statsLoading || statsError ? "" : "kg CO2e"}
                            caption="inputAmt x 66g 기준"
                        />
                    </div>

                    {statsError ? <p role="alert">{statsError} <button type="button" className={style["button--secondary"]} onClick={() => setReloadStats((value) => value + 1)}>다시 시도</button></p> : null}

                    <ChartPanel title={mode === "ALL" ? "일일 총 회수 추이" : "선택 대상 일일 회수 추이"} summary={`${modeSummary} · 막대를 클릭하면 해당 날짜의 내역을 확인할 수 있습니다.`}
                        action={<button type="button" className={style["button--secondary"]} disabled={statsLoading} onClick={() => setReloadStats((value) => value + 1)}>새로고침</button>}>
                        {statsLoading ? <div className={style["empty-state"]} role="status"><LoadingIndicator>회수 통계를 불러오고 있습니다.</LoadingIndicator></div> : statsError ? <div className={style["empty-state"]}>통계를 불러오지 못했습니다.</div> :
                            <Bars data={receiptDaily} labelKey="date" dataKey="value" unit="건" onSelect={setHistoryDate} />}
                    </ChartPanel>
                    <ChartPanel title="회원 증가 추이" summary="기간 내 신규 가입 회원 수">
                        <Lines data={userDaily} labelKey="date" dataKey="value" unit="명" />
                    </ChartPanel>
                    <ChartPanel title="키오스크 설치 추이" summary="기간 내 신규 설치 키오스크 수">
                        <Bars data={kioskDaily} labelKey="date" dataKey="value" unit="대" />
                    </ChartPanel>
                </div>
            </div>

            {historyDate && collectionStats && <CollectionHistoryModal target={{ kind: "day", date: historyDate, filters, summary: modeSummary,
                count: collectionStats.days.find((day) => day.date === historyDate)?.count || 0,
                quantity: collectionStats.days.find((day) => day.date === historyDate)?.quantity || 0 }} close={() => setHistoryDate(null)} />}

            {selectUsersOpen && (
                <SelectModal
                    title="유저 선택"
                    description="통계에 반영할 유저를 선택합니다."
                    searchPlaceholder="이름, 전화번호 또는 사용자 ID로 검색"
                    headers={["선택", "이름", "전화번호", "사용자 ID"]}
                    rows={usersInRange.map((user) => ({
                        id: user.uid,
                        columns: [user.name, user.phone ? formatPhone(user.phone) : "전화번호 없음", user.uid],
                    }))}
                    selected={selectedUsers}
                    close={() => setSelectUsersOpen(false)}
                    onConfirm={setSelectedUsers}
                />
            )}

            {selectKiosksOpen && (
                <SelectModal
                    title="키오스크 선택"
                    description="통계에 반영할 키오스크를 선택합니다."
                    searchPlaceholder="이름, ID 또는 주소로 검색"
                    headers={["선택", "이름", "주소", "키오스크 ID"]}
                    rows={kiosks.map((kiosk) => ({
                        id: kiosk.docId,
                        columns: [kiosk.name || "이름 없음", kiosk.address || "주소 없음", kiosk.docId],
                    }))}
                    selected={selectedKiosks}
                    close={() => setSelectKiosksOpen(false)}
                    onConfirm={setSelectedKiosks}
                />
            )}
        </>
    );
};

export default AdminStats;

const SelectModal = ({
    title,
    description,
    searchPlaceholder,
    headers,
    rows,
    selected,
    close,
    onConfirm,
}: {
    title: string;
    description: string;
    searchPlaceholder: string;
    headers: string[];
    rows: SelectableRow[];
    selected: Set<string>;
    close: () => void;
    onConfirm: (selected: Set<string>) => void;
}) => {
    const [search, setSearch] = useState<string>("");
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set(selected));
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    const filteredRows = rows.filter((row) => {
        const keyword = search.toLowerCase();
        return search === "" || row.columns.some((column) => column.toLowerCase().includes(keyword)) || row.id.toLowerCase().includes(keyword);
    });

    const visibleIds = filteredRows.map((row) => row.id);
    const isAllSelected = visibleIds.length > 0 && visibleIds.every((id) => localSelected.has(id));
    const isIndeterminate = visibleIds.some((id) => localSelected.has(id)) && !isAllSelected;

    const toggleOne = (id: string) => {
        const next = new Set(localSelected);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setLocalSelected(next);
    };

    const toggleAll = () => {
        const next = new Set(localSelected);
        if (isAllSelected) {
            visibleIds.forEach((id) => next.delete(id));
        } else {
            visibleIds.forEach((id) => next.add(id));
        }
        setLocalSelected(next);
    };

    const handleConfirm = () => {
        onConfirm(localSelected);
        hide();
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>통계 대상</p>
                        <h4 className={style["modal-title"]}>{title}</h4>
                        <p className={style["modal-copy"]}>{description}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["select-toolbar"]}>
                        <input
                            className={style["input--option-filter"]}
                            type="text"
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className={style["button--secondary"]} onClick={toggleAll} disabled={visibleIds.length === 0}>
                            {isAllSelected ? "현재 목록 선택 해제" : "현재 목록 전체선택"}
                        </button>
                    </div>
                    <div className={style["container--options"]}>
                        <div className={style["unit--option-title"]}>
                            <div className={style["text--option"]}>
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isIndeterminate;
                                    }}
                                    onChange={toggleAll}
                                />
                                전체
                            </div>
                            {headers.slice(1).map((header) => (
                                <p key={header} className={style["text--option"]}>
                                    {header}
                                </p>
                            ))}
                        </div>
                        {filteredRows.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                        {filteredRows.map((row) => (
                            <SelectableListRow
                                key={row.id}
                                row={row}
                                selected={localSelected.has(row.id)}
                                onToggle={() => toggleOne(row.id)}
                            />
                        ))}
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={handleConfirm}>
                        확인 ({localSelected.size}개)
                    </button>
                </div>
            </div>
        </div>
    );
};

const SelectableListRow = ({
    row,
    selected,
    onToggle,
}: {
    row: SelectableRow;
    selected: boolean;
    onToggle: () => void;
}) => (
    <div className={style["unit--option-list"]} onClick={onToggle}>
        <div className={style["text--option"]}>
            <input
                type="checkbox"
                checked={selected}
                onChange={onToggle}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            />
        </div>
        {row.columns.map((column, index) => (
            <p key={`${row.id}-${index}`} className={style["text--option"]}>
                {column}
            </p>
        ))}
    </div>
);

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone || "";
};

const getModeSummary = (mode: Mode, kioskCount: number, userCount: number) => {
    if (mode === "KIOSK") return kioskCount > 0 ? `선택 키오스크 ${kioskCount}개 기준` : "전체 키오스크 기준";
    if (mode === "USER") return userCount > 0 ? `선택 유저 ${userCount}명 기준` : "전체 유저 기준";
    return "전체 기준";
};

const SummaryCard: React.FC<{ title: string; value: number | string; unit: string; caption?: string; loading?: boolean }> = ({
    title,
    value,
    unit,
    caption,
    loading = false,
}) => (
    <div className={style["summary-card"]}>
        <p>{title}</p>
        <strong aria-busy={loading}>
            {loading ? <LoadingIndicator>조회 중</LoadingIndicator> : value}
            <span>{unit}</span>
        </strong>
        {caption && <small>{caption}</small>}
    </div>
);

const ChartPanel: React.FC<{ title: string; summary: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, summary, children, action }) => (
    <section className={style["chart-panel"]}>
        <div className={style["chart-header"]}>
            <div>
                <h5>{title}</h5>
                <p>{summary}</p>
            </div>
            {action}
        </div>
        <div className={style["chart-body"]}>{children}</div>
    </section>
);

const Lines: React.FC<{
    data: any[];
    labelKey: string;
    dataKey: string;
    unit?: string;
}> = ({ data, labelKey, dataKey, unit }) => {
    if (!data.some((item) => item[dataKey] > 0)) return <div className={style["empty-state"]}>표시할 데이터가 없습니다.</div>;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey={labelKey} tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: any) => `${value}${unit ?? ""}`} />
                <Legend />
                <Line type="monotone" dataKey={dataKey} name="값" dot={false} stroke="#61A6E6" strokeWidth={2} />
            </LineChart>
        </ResponsiveContainer>
    );
};

const Bars: React.FC<{
    data: any[];
    labelKey: string;
    dataKey: string;
    unit?: string;
    onSelect?: (date: string) => void;
}> = ({ data, labelKey, dataKey, unit, onSelect }) => {
    if (!data.some((item) => item[dataKey] > 0)) return <div className={style["empty-state"]}>표시할 데이터가 없습니다.</div>;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey={labelKey} tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(value: any) => `${value}${unit ?? ""}`} />
                <Legend />
                <Bar dataKey={dataKey} name="값" fill="#61A6E6" radius={[6, 6, 0, 0]}
                    onClick={onSelect ? (entry: any) => { const date = entry.payload?.[labelKey]; if (typeof date === "string") onSelect(date); } : undefined}
                    shape={onSelect ? (props: any) => <Rectangle {...props} role="button" tabIndex={0} style={{ cursor: "pointer", pointerEvents: "all" }}
                        aria-label={`${props.payload?.[labelKey]} 회수 ${props.payload?.[dataKey]}건 내역 보기`}
                        onClick={(event: React.MouseEvent<SVGPathElement>) => { event.stopPropagation(); const date = props.payload?.[labelKey]; if (typeof date === "string") onSelect(date); }}
                        onKeyDown={(event: React.KeyboardEvent<SVGPathElement>) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelect(props.payload[labelKey]); } }} /> : undefined} />
            </BarChart>
        </ResponsiveContainer>
    );
};
